import { Mark, mergeAttributes } from '@tiptap/core'
import { Plugin, PluginKey } from '@tiptap/pm/state'

export interface LinkOptions {
  /**
   * If enabled, links will be opened on click.
   */
  openOnClick: boolean
  /**
   * If enabled, links will be validated before being set.
   */
  validate?: (url: string) => boolean
  /**
   * HTML attributes to add to the link element.
   */
  HTMLAttributes: Record<string, any>
}

export const LinkExtension = Mark.create<LinkOptions>({
  name: 'link',

  priority: 1000,

  keepOnSplit: false,

  inclusive() {
    return false
  },

  addOptions() {
    return {
      openOnClick: false,
      validate: undefined,
      HTMLAttributes: {
        class: 'text-primary underline underline-offset-[3px] hover:text-primary/80 cursor-pointer transition-colors',
      },
    }
  },

  addAttributes() {
    return {
      href: {
        default: null,
      },
      target: {
        default: null,
        // When target is '_blank', render it; otherwise don't add target attribute
        renderHTML: (attributes) => {
          if (attributes.target === '_blank') {
            return { target: '_blank' }
          }
          return {}
        },
      },
      rel: {
        default: null,
        // When target is '_blank', also set rel="noopener noreferrer nofollow"
        renderHTML: (attributes) => {
          if (attributes.target === '_blank') {
            return { rel: 'noopener noreferrer nofollow' }
          }
          return {}
        },
      },
      class: {
        default: null,
      },
    }
  },

  parseHTML() {
    return [
      {
        tag: 'a[href]',
        getAttrs: (dom) => {
          const element = dom as HTMLElement
          const href = element.getAttribute('href')
          const target = element.getAttribute('target')
          const rel = element.getAttribute('rel')

          // Validate URL if validation function is provided
          if (this.options.validate && href && !this.options.validate(href)) {
            return false
          }

          return {
            href,
            target,
            rel,
          }
        },
      },
    ]
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'a',
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes),
      0,
    ]
  },

  addCommands() {
    return {
      setLink:
        (attributes) =>
        ({ chain }) => {
          return chain().setMark(this.name, attributes).run()
        },

      toggleLink:
        (attributes) =>
        ({ chain }) => {
          return chain()
            .toggleMark(this.name, attributes, { extendEmptyMarkRange: true })
            .run()
        },

      unsetLink:
        () =>
        ({ chain }) => {
          return chain()
            .unsetMark(this.name, { extendEmptyMarkRange: true })
            .run()
        },
    }
  },

  addProseMirrorPlugins() {
    const plugins: Plugin[] = []

    if (this.options.openOnClick) {
      plugins.push(
        new Plugin({
          key: new PluginKey('handleClickLink'),
          props: {
            handleClick: (view, pos, event) => {
              const { schema, doc } = view.state
              const range = doc.resolve(pos)
              const marks = range.marks()
              const link = marks.find((mark) => mark.type === schema.marks.link)

              if (link && link.attrs.href) {
                const target = link.attrs.target || '_self'
                window.open(link.attrs.href, target)
                return true
              }

              return false
            },
          },
        }),
      )
    }

    return plugins
  },
})
